import { forwardRef } from 'react'
import { type ApexOptions } from 'apexcharts'
import merge from 'deepmerge'
import Chart from 'react-apexcharts'

import { GRAPH_COLORS } from 'consts'
import { humanizeLargeNumber } from 'utils'

type seriesObjectType = {
  name: string
  data: number[]
}

export type chartData = {
  name: string
  data: {
    x: string
    y: string | number
  }[]
  customSettings: Record<string, any>
}

interface IStackedColumnsChart {
  colors?: string[]
  categories?: string[]
  series: seriesObjectType[] | chartData[]
  options?: Record<string, any>
  height?: number | string
  width?: number | string
  id?: string
}

const StackedColumnsChart = forwardRef(
  (
    {
      colors = GRAPH_COLORS,
      categories = [],
      series = [],
      options = {},
      height = 350,
      width,
      id,
      ...props
    }: IStackedColumnsChart,
    ref: React.Ref<any>
  ) => {
    const baseOptions: ApexOptions = {
      chart: {
        id,
        stacked: true,
        stackType: '100%',
        fontFamily: 'Muli, Arial, sans-serif',
        events: {
          mounted: (chart: any) => {
            chart.windowResizeHandler()
          },
        },
        toolbar: {
          show: true,
          tools: {
            download: false,
            pan: false,
            reset: false,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
          },
        },
        animations: {
          enabled: false,
        },
      },
      xaxis: {
        categories,
      },
      yaxis: {
        labels: {
          formatter: function (val: number, chartData: Record<string, any>) {
            const isAxisValue = chartData?.dataPointIndex === -1 || typeof chartData === 'number'

            if (isAxisValue) {
              return `${val}%`
            }

            return humanizeLargeNumber(val, 2)
          },
        },
      },
      colors: colors,
      fill: {
        opacity: 1,
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        offsetY: 4,
        markers: {
          shape: 'circle',
          size: 6,
        },
      },
    }

    const mergedOptions = merge.all([baseOptions, options], {
      arrayMerge: (destination, source) => source,
    })

    return (
      <Chart
        ref={ref}
        type="bar"
        width={width}
        height={height}
        series={series}
        options={mergedOptions}
        id={id}
        {...props}
      />
    )
  }
)

export default StackedColumnsChart
